import {
  Box,
  Button,
  Chip,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,  
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  Dialog,
  DialogContent,
  DialogActions,
  Pagination,
  Switch,
  IconButton,
  Tooltip,
  Autocomplete,
  Avatar,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useState, useCallback, useMemo, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import WifiIcon from "@mui/icons-material/Wifi";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import LockIcon from "@mui/icons-material/Lock";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { layoutCSS } from "../component/common/commonStyle";
import { useGetTemplatesQuery, useGetPhoneNUmberQuery } from "../store/services/whatsappApi";
import {
  useGetcategoryQuery,
  useGetcategoryidQuery,
  useGetcamplaignlistbyidQuery,
  useGetrcstemplateforeventQuery,
} from "../store/services/rcstemplateApi";
import { useGetFlowsQuery } from "../store/services/flowApi";
import { useGetTemplateByPrincipalIdQuery } from "../store/services/templateApi";
import { useFetchPrincipalEntitiesdataQuery } from "../store/services/principleApi";
import {
  useGeteventListQuery,
  useGetdbfildsFilterQuery,
  useCreateEventFlowMutation,
  useGetprefilledvalueQuery,
  useSetactiveinativeeventMutation,
} from "../store/services/createDbApi";
import { jwtDecode } from "jwt-decode";
import { maskNumber } from "../component/common/Encryption";

// ── Shared style tokens ───────────────────────────────────────────────────
const glassInput = {
  background: "rgba(255,255,255,0.7)",
  backdropFilter: "blur(8px)",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#CBD5E1",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#0288d1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#0288d1",
    borderWidth: "1.5px",
  },
};

const smallSelectSx = {
  fontSize: "0.75rem",
  ...glassInput,
  "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.05)" },
};

const nameFieldSx = {
  "& .MuiInputBase-input": { padding: "6px 8px", fontSize: "0.8rem" },
  "& .MuiOutlinedInput-root": { height: "32px", ...glassInput },
  width: "260px",
};

const chipAreaSx = {
  flexWrap: "wrap",
  gap: 1,
  p: 1.25,
  border: "1px dashed #ccc",
  borderRadius: "10px",
  background: "rgba(255,255,255,0.5)",
  backdropFilter: "blur(6px)",
  minHeight: 36,
};

const thCell = {
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "white",
  borderRight: "0.5px solid #3a3a3a",
  py: 0.75,
};

const tdCell = {
  padding: "6px 8px",
  fontSize: "0.78rem",
  borderRight: "0.5px solid #d0d0d0",
};

const BRAND = "#0288d1";

const sectionSx = {
  border: "1px solid #E6ECF1",
  borderRadius: "10px",
  p: 1.75,
  background: "#FAFCFE",
  mt: 1.5,
};

const sectionLabelSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  fontSize: "0.74rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#4A5A68",
  mb: 1.25,
};

const SERVICE_OPTIONS = [
  { value: "flow", label: "Flow" },
  { value: "sms", label: "SMS" },
  { value: "rcs", label: "RCS" },
  { value: "whatsapp", label: "WhatsApp" },
];

const RUN_DURATION_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

const OPERATION_OPTIONS = [
  "equals",
  "in",
  "contain",
  "greater_than",
  "less_than",
  "between",
  "start with",
  "end with",
  "is",
  "before",
  "after",
];

const WEEKDAY_OPTIONS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const CAMPAIGN_TYPES = [
  { value: "repeat", label: "Repeat", desc: "Runs on a recurring schedule", icon: RepeatRoundedIcon },
  { value: "once", label: "Once", desc: "Runs once at a set time", icon: EventRoundedIcon },
  { value: "realtime", label: "Real Time", desc: "Fires instantly, no schedule needed", icon: BoltRoundedIcon },
];

const IST_OFFSET = "+05:30";
const pad2 = (n) => String(n).padStart(2, "0");

const buildISTDateTime = (localDateTime) => {
  if (!localDateTime) return null;
  const withSeconds = localDateTime.length === 16 ? `${localDateTime}:00` : localDateTime;
  return `${withSeconds}${IST_OFFSET}`;
};

const getCurrentISTDateTime = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(new Date())
    .reduce((values, part) => {
      if (part.type !== "literal") values[part.type] = part.value;
      return values;
    }, {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
};

const isFutureSchedule = (value) => {
  if (!value) return false;
  const now = getCurrentISTDateTime();
  const dateTime = /^\d{2}:\d{2}$/.test(value)
    ? `${now.slice(0, 10)}T${value}:00`
    : value.length === 16
      ? `${value}:00`
      : value;
  return dateTime > now;
};

const normalizeTimeValue = (value) => {
  if (!value && value !== 0) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const match = raw.match(/(\d{1,2}:\d{2})(?::\d{2})?/);
  if (match) return match[1];

  const timePart = raw.includes("T") ? raw.split("T")[1] : raw.includes(" ") ? raw.split(" ")[1] : raw;
  const timeMatch = String(timePart).match(/(\d{1,2}:\d{2})(?::\d{2})?/);
  if (timeMatch) return timeMatch[1];

  return raw;
};

export const normalizeDateTimeLocalValue = (value) => {
  if (!value && value !== 0) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const isoLike = raw.replace(" ", "T");
  const match = isoLike.match(/(\d{4}-\d{2}-\d{2})T(\d{1,2}:\d{2})(?::\d{2})?/);
  if (match) return `${match[1]}T${match[2]}`;

  const timeOnly = normalizeTimeValue(raw);
  if (timeOnly && /^\d{1,2}:\d{2}$/.test(timeOnly)) return timeOnly;

  return raw;
};

const extractRunAtTime = (value) => {
  if (!value && value !== 0) return null;
  const normalized = normalizeTimeValue(value);
  if (!normalized) return null;
  return normalized.length === 5 ? `${normalized}:00` : normalized;
};

const nowISTString = () => {
  const d = new Date();
  const local = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  return `${local}${IST_OFFSET}`;
};

const statusStyle = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "APPROVED") return { bg: "#E4F6E9", color: "#1B7A3D" };
  if (s === "REJECTED") return { bg: "#FDECEA", color: "#D32F2F" };
  return { bg: "#FFF4D9", color: "#8A6D00" };
};

const getOTPButtonLabel = (otpType) => {
  switch ((otpType || "").toUpperCase()) {
    case "ONE_TAP":
      return "Autofill";
    case "COPY_CODE":
      return "Copy Code";
    case "ZERO_TAP":
      return "Continue";
    default:
      return "Verify";
  }
};

const isVideoUrl = (url = "") => /\.(mp4|mov|webm)(\?.*)?$/i.test(url || "");

const getButtonTooltip = (btn) => {
  if (btn?.phone_number) return btn.phone_number;
  if (btn?.url) return btn.url;
  if (btn?.suggestion_postback) return btn.suggestion_postback;
  return "";
};

function HeaderMedia({ url, format, height = 120 }) {
  if (!url) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#eee" }}>
        <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>No media</Typography>
      </Box>
    );
  }
  if (format === "VIDEO") {
    return (
      <video
        src={url}
        controls
        style={{ width: "100%", height, objectFit: "cover", display: "block" }}
      />
    );
  }
  return (
    <img
      src={url}
      alt="header"
      style={{ width: "100%", height, objectFit: "cover", display: "block" }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
}

function CarouselPreviewGray({ cards = [] }) {
  const [idx, setIdx] = useState(0);
  if (!cards.length) return null;
  const card = cards[idx];
  const header = card?.components?.find((c) => c.type === "HEADER");
  const body = card?.components?.find((c) => c.type === "BODY");
  const buttons = card?.components?.find((c) => c.type === "BUTTONS");
  const mediaUrl = header?.example?.header_handle?.[0];

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", overflow: "hidden", mb: 1 }}>
      {mediaUrl && <HeaderMedia url={mediaUrl} format={header?.format} height={110} />}
      <Box sx={{ p: 1.5 }}>
        {body?.text && <Typography sx={{ fontSize: "0.78rem", color: "#333" }}>{body.text}</Typography>}
        {buttons?.buttons?.length > 0 && (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {buttons.buttons.map((btn, i) => (
              <Button
                key={i}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ fontSize: "0.68rem", textTransform: "none", borderRadius: "6px" }}
              >
                {btn.text}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
      {cards.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, pb: 1 }}>
          {cards.map((_, i) => (
            <Box
              key={i}
              onClick={() => setIdx(i)}
              sx={{
                width: i === idx ? 14 : 6,
                height: 6,
                borderRadius: 3,
                cursor: "pointer",
                bgcolor: i === idx ? "#128C7E" : "#ccc",
                transition: "all .2s ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function CalendarDatePicker({ selectedDates, onToggleDate }) {
  const daysInMonth = 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <Box sx={{
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: 0.4,
      maxWidth: "100%",
    }}>
      {days.map((date) => (
        <Box
          key={date}
          onClick={() => onToggleDate(date)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            borderRadius: "7px",
            cursor: "pointer",
            fontSize: "0.76rem",
            fontWeight: 500,
            transition: "all 0.15s ease",
            background: selectedDates.includes(date) ? BRAND : "transparent",
            color: selectedDates.includes(date) ? "#fff" : "#4A5A68",
            border: selectedDates.includes(date) ? `1px solid ${BRAND}` : "1px solid #E0E7ED",
            "&:hover": {
              background: selectedDates.includes(date) ? "#01579b" : "#EAF4FB",
              borderColor: BRAND,
            },
          }}
        >
          {date}
        </Box>
      ))}
    </Box>
  );
}

function EventCreation() {
  // ── Form State ─────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [campaignType, setCampaignType] = useState("");
  const [eventName, setEventName] = useState("");
  const [editingEventId, setEditingEventId] = useState(null);
  const [filemanage, setFilemanage] = useState(0);

  // ── Event Data Filter State ────────────────────────────────────────
  const [selected, setSelected] = useState("");
  const [chips, setChips] = useState([]);
  const [selectedFields, setSelectedFields] = useState([""]);
  const [dataTypes, setDataTypes] = useState([""]);
  const [operations, setOperations] = useState([""]);
  const [values, setValues] = useState([""]);
  const [logicalOperators, setLogicalOperators] = useState(["AND"]);

  // ── Split / batch configuration ────────────────────────────────────
  const [fileSplitType, setFileSplitType] = useState(0);
  const [useFilesOfSplitType, setUseFilesOfSplitType] = useState(0);
  const [furtherSplit, setFurtherSplit] = useState(false);
  const [furtherSplitNoOfRecords, setFurtherSplitNoOfRecords] = useState("");
  const [furtherSplitTimeGap, setFurtherSplitTimeGap] = useState("");

  // ── Destination ────────────────────────────────────────────────────
  const [serviceType, setServiceType] = useState("");
  const [selectedFlow, setSelectedFlow] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedRcsCategory, setSelectedRcsCategory] = useState("");
  const [selectedRcsBotId, setSelectedRcsBotId] = useState("");
  const [selectedWhatsAppPhoneId, setSelectedWhatsAppPhoneId] = useState("");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);

  // ── Schedule ─────────────────────────────────────────────────────
  const [runDuration, setRunDuration] = useState("");
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [recordsPerPage, setRecordsPerPage] = useState("");
  const [batchLimit, setBatchLimit] = useState("");
  const [scheduleFileCreation, setScheduleFileCreation] = useState(false);
  const [fileCreationDateTime, setFileCreationDateTime] = useState("");

  // ── Prefilled event data (edit mode) ──────────────────────────────
  const { data: prefilleddata } = useGetprefilledvalueQuery(
     editingEventId,
    { skip: !editingEventId }
  );

  // ── Events list ───────────────────────────────────────────────────
  const [eventsPage, setEventsPage] = useState(1);
  const eventsPerPage = 10;

  // ── Delete confirmation ───────────────────────────────────────────
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // ── Static data ───────────────────────────────────────────────────
  const { data: getdbfilterfield } = useGetdbfildsFilterQuery();
  const rawColumns = getdbfilterfield?.data || [];
  const columns = rawColumns.flat();
  const fieldList = columns.map((c) => c.column_name);

  const [customerId, setCustomerId] = useState(null);

  const {
    data: principalEntitiesData,
    isLoading: principalEntitiesLoading,
    isError: principalEntitiesError,
  } = useFetchPrincipalEntitiesdataQuery();
  const principalEntities = principalEntitiesData?.data || [];
  const { data: whatsAppPhoneData, isLoading: whatsAppPhoneLoading, isError: whatsAppPhoneError } =
    useGetPhoneNUmberQuery(customerId, {
      skip: !customerId || serviceType !== "whatsapp",
    });
  const whatsAppPhoneNumbers = whatsAppPhoneData?.phone_numbers || [];
  const selectedWhatsAppPhone = whatsAppPhoneNumbers.find(
    (phone) => String(phone.phone_number_id) === String(selectedWhatsAppPhoneId)
  );
  const selectedWhatsAppWabaId = selectedWhatsAppPhone?.waba_id || "";

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return;
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (id) setCustomerId(id);
    } catch (err) {
      // ignore invalid token
    }
  }, []);

  useEffect(() => {
    setSelectedCardIndex(0);
  }, [selectedTemplate, previewOpen]);

  const {
    data: eventsListData = {},
    isLoading: eventsLoading,
    isError: eventsError,
    refetch: refetchEvents,
  } = useGeteventListQuery(
    { customer_id: customerId },
    { skip: !customerId }
  );

  const [createEventFlow, { isLoading: isSavingFlow }] = useCreateEventFlowMutation();
  const [setActiveInactiveEvent, { isLoading: isUpdatingEventStatus }] =
    useSetactiveinativeeventMutation();

  const eventsList = useMemo(() => {
    const apiEvents = eventsListData?.data || [];
    return apiEvents.map((event) => ({
      ...event,
      event_id: event.event_id,
      event_name: event.event_name,
      data_filter: {
        requiredData: event.fieldinfile || event.filter_data_jsonb?.requiredData || "",
        jsonData: event.event_data_filter || event.filter_data_jsonb?.jsonData || [],
      },
      is_active: event.is_active,
      version: event.version,
      created_at: event.created_at,
      next_run_at: event.next_run_at,
    }));
  }, [eventsListData]);

  const { data: waTemplateData = {}, isLoading: waTemplatesLoading } = useGetTemplatesQuery(
    { customer_id: customerId },
    { skip: !customerId || serviceType !== "whatsapp" || !selectedWhatsAppWabaId }
  );

  const waTemplates = (waTemplateData?.templates ?? [])
    .filter((template) => String(template.waba_id) === String(selectedWhatsAppWabaId))
    .filter((t) => t.status?.toUpperCase() === "APPROVED")
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const { data: rcsCategoriesResp = {}, isLoading: rcsCategoriesLoading } = useGetcategoryQuery(
    undefined,
    { skip: serviceType !== "rcs" }
  );
  const rcsCategories = rcsCategoriesResp?.data ?? [];

  const { data: rcsBotsResp = {}, isLoading: rcsBotsLoading } = useGetcategoryidQuery(
    selectedRcsCategory,
    { skip: !selectedRcsCategory || serviceType !== "rcs" }
  );
  const rcsBots = rcsBotsResp?.data ?? [];
  const selectedRcsBot = rcsBots.find(
    (bot) => String(bot.bot_id) === String(selectedRcsBotId)
  );

  const { data: rcsBotTemplatesResp = {}, isLoading: rcsTemplatesLoading } = useGetcamplaignlistbyidQuery(
    selectedRcsBotId,
    { skip: !selectedRcsBotId || serviceType !== "rcs" }
  );
  const rcsBotTemplateData = rcsBotTemplatesResp?.data?.[0]?.template_by_bot_customer?.templates || [];
  const rcsTemplates = rcsBotTemplateData
    .filter((template) => template.status?.toLowerCase() === "approved")
    .map((t) => ({ id: t.template_id || t.rcs_template_id || t.id, name: t.template_name }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const { data: flowsResp = {}, isLoading: flowsLoading } = useGetFlowsQuery(
    { page: 1, search: "" },
    { skip: serviceType !== "flow" }
  );

  const fetchedFlows = (flowsResp?.data ?? []).map((f) => ({ flow_id: f.cf_id || f.flow_id, flow_name: f.cf_name || f.flow_name }));

  const { data: smsResp = {}, isLoading: smsLoading } = useGetTemplateByPrincipalIdQuery(
    selectedEntityId,
    { skip: !selectedEntityId || serviceType !== "sms" }
  );

  const smsTemplates = (smsResp?.data ?? []).map((t) => ({
    template_id: t.template_id || t.id,
    template_name: t.template_name || t.name,
    template_text: t.template_text || "",
    template_type: t.template_type || "",
    trai_template_id: t.trai_template_id || "",
    header_name: t.header_name || "",
  }));

  const { data: rcsEventTemplateResp, isLoading: rcsEventTemplateLoading } = useGetrcstemplateforeventQuery(
    { template_name: selectedTemplate },
    { skip: !selectedTemplate || serviceType !== "rcs" }
  );

  const rcsEventTemplate = rcsEventTemplateResp?.data;

  const totalEvents = eventsList.length;
  const totalPages = Math.ceil(totalEvents / eventsPerPage);
  const paginatedEvents = eventsList.slice(
    (eventsPage - 1) * eventsPerPage,
    eventsPage * eventsPerPage
  );

  // ── Effect A: fill form fields when prefilled data arrives ────────
  useEffect(() => {
    if (!prefilleddata?.data) return;
    const item = prefilleddata.data;

    // Basic info
    setEventName(item.event_name || "");

    // Event Data Filter
    const req = item.filter_data_jsonb?.requiredData
      ? item.filter_data_jsonb.requiredData.split(",").filter(Boolean)
      : [];
    const conditions = item.filter_data_jsonb?.jsonData?.length
      ? item.filter_data_jsonb.jsonData
      : [{}];

    setChips(req);
    setSelectedFields(conditions.map((c) => c.fieldName || ""));
    setDataTypes(
      conditions.map(
        (c) => columns.find((col) => col.column_name === c.fieldName)?.data_type || ""
      )
    );
    setOperations(conditions.map((c) => c.searchType || ""));
    setValues(conditions.map((c) => (c.value != null ? String(c.value) : "")));
    setLogicalOperators(conditions.map((c) => String(c.logicalOperator || "AND").toUpperCase()));

    // Campaign type
    if (item.repeat_type) setCampaignType(item.repeat_type);

    // Schedule
    if (item.repeat_type === "repeat") {
      if (item.repeat_period) setRunDuration(item.repeat_period);

      if (item.run_at_time) {
        setScheduleDateTime(normalizeTimeValue(item.run_at_time));
      }

      if (item.custom_period === "weekdays" && Array.isArray(item.periods_values)) {
        setSelectedDays(item.periods_values.map((d) => String(d).toUpperCase()));
      } else if (item.repeat_period === "monthly" && Array.isArray(item.periods_values)) {
        setSelectedDates(item.periods_values.map(Number));
      }
    } else if (item.repeat_type === "once" && item.once_datetime) {
      setScheduleDateTime(normalizeDateTimeLocalValue(item.once_datetime));
    }

    // Split / batch
    setFurtherSplit(!!item.further_split);
    if (item.further_split_noofrecords != null) {
      setFurtherSplitNoOfRecords(String(item.further_split_noofrecords));
    }
    if (item.further_split_time_gap != null) {
      setFurtherSplitTimeGap(String(item.further_split_time_gap));
    }

    // File management
    if (item.records_per_page && item.batch_limit) {
      setFilemanage(2);
      setRecordsPerPage(String(item.records_per_page));
      setBatchLimit(String(item.batch_limit));
    } else if (item.batch_limit) {
      setFilemanage(1);
      setBatchLimit(String(item.batch_limit));
    } else {
      setFilemanage(0);
    }

    // Entity (SMS) — only if backend returns pe_id
    if (item.pe_id) {
      const entity = principalEntities.find(
        (e) => String(e.pe_id) === String(item.pe_id)
      );
      if (entity) {
        setSelectedEntityId(entity.principalentity_id);
        setSelectedEntity(entity);
      }
    }

    // Service — triggers the template / flow list fetch
    if (item.msg_type) setServiceType(item.msg_type.toLowerCase());
    if (item.phone_number_id) setSelectedWhatsAppPhoneId(item.phone_number_id);
    const savedRcsCategory = item.rcs_bot_type_id || item.category_id || item.bot_type_id;
    if (savedRcsCategory) setSelectedRcsCategory(String(savedRcsCategory));
    if (item.bot_id) setSelectedRcsBotId(String(item.bot_id));

    // ✅ Fallback: show the saved template name until the id resolves
    if (item.msg_template) setSelectedTemplate(item.msg_template);

    // ✅ Fallback for flow too
    if (item.msg_type?.toLowerCase() === "flow" && item.msg_template) {
      setSelectedFlow(item.msg_template);
    }
  }, [prefilleddata, columns, principalEntities]);

  // ── Effect B: resolve template/flow ID once lists have loaded ─────
  useEffect(() => {
    if (!prefilleddata?.data) return;
    const item = prefilleddata.data;
    if (!item.msg_template || !item.msg_type) return;

    const msgType = item.msg_type.toLowerCase();

    if (msgType === "flow" && fetchedFlows.length) {
      const flow = fetchedFlows.find((f) => f.flow_name === item.msg_template);
      if (flow) setSelectedFlow(flow.flow_id);

    } else if (msgType === "whatsapp" && waTemplates.length) {
      const tpl = waTemplates.find((t) => t.name === item.msg_template);
      if (tpl) setSelectedTemplate(tpl.template_id);

    } else if (msgType === "sms" && smsTemplates.length) {
      const tpl = smsTemplates.find((t) => t.template_name === item.msg_template);
      if (tpl) setSelectedTemplate(tpl.template_id);

    } else if (msgType === "rcs") {
      setSelectedTemplate(item.msg_template);
    }
  }, [prefilleddata, fetchedFlows, waTemplates, smsTemplates]);

  // ── Form Handlers ─────────────────────────────────────────────────
  const handleCreateNewEvent = () => {
    resetEventForm();
    setShowForm(true);
  };

  const handleCampaignTypeSelect = (type) => {
    setCampaignType(type);
  };

  const handleCancelForm = () => {
    resetEventForm();
    setShowForm(false);
  };

  const handleFilterFieldSelect = (e) => {
    const v = e.target.value;
    if (!chips.includes(v)) setChips((p) => [...p, v]);
    setSelected("");
  };

  const handleFilterChipDelete = (chip) => setChips((p) => p.filter((x) => x !== chip));

  const onFilterDragEnd = useCallback(
    (result) => {
      if (!result.destination) return;
      const r = Array.from(chips);
      const [moved] = r.splice(result.source.index, 1);
      r.splice(result.destination.index, 0, moved);
      setChips(r);
    },
    [chips]
  );

  const handleFieldChange = (index, fieldName) => {
    const f = [...selectedFields], d = [...dataTypes], o = [...operations], v = [...values];
    const col = columns.find((c) => c.column_name === fieldName);
    f[index] = fieldName; d[index] = col?.data_type || ""; o[index] = ""; v[index] = "";
    setSelectedFields(f); setDataTypes(d); setOperations(o); setValues(v);
  };

  const handleOperationChange = (index, op) => {
    const o = [...operations], v = [...values];
    o[index] = op; v[index] = "";
    setOperations(o); setValues(v);
  };

  const handleValueChange = (index, val) => {
    const v = [...values]; v[index] = val; setValues(v);
  };

  const handleLogicalOperatorChange = (index, operator) => {
    const next = [...logicalOperators];
    next[index] = operator;
    setLogicalOperators(next);
  };

  const handleAddRow = () => {
    if (selectedFields.length < fieldList.length) {
      setSelectedFields([...selectedFields, ""]); setDataTypes([...dataTypes, ""]);
      setOperations([...operations, ""]); setValues([...values, ""]);
      setLogicalOperators([...logicalOperators, "AND"]);
    }
  };

  const handleRemoveRow = (index) => {
    if (selectedFields.length > 1) {
      setSelectedFields(selectedFields.filter((_, i) => i !== index));
      setDataTypes(dataTypes.filter((_, i) => i !== index));
      setOperations(operations.filter((_, i) => i !== index));
      setValues(values.filter((_, i) => i !== index));
      setLogicalOperators(logicalOperators.filter((_, i) => i !== index));
    }
  };

  const isDateLike = (t = "") => /(date|time|timestamp|datetime)/i.test(t);

  const resetEventForm = () => {
    setEventName(""); setEditingEventId(null); setChips([]); setSelected("");
    setSelectedFields([""]); setDataTypes([""]); setOperations([""]); setValues([""]);
    setLogicalOperators(["AND"]);
    setFileSplitType(0); setUseFilesOfSplitType(0); setFurtherSplit(false);
    setFurtherSplitNoOfRecords(""); setFurtherSplitTimeGap("");
    setCampaignType("");
    setServiceType(""); setSelectedFlow(""); setSelectedTemplate("");
    setSelectedRcsCategory(""); setSelectedRcsBotId("");
    setSelectedWhatsAppPhoneId("");
    setSelectedEntityId(""); setSelectedEntity(null);
    setRunDuration(""); setScheduleDateTime("");
    setSelectedDays([]); setSelectedDates([]);
    setRecordsPerPage(""); setBatchLimit(""); setFilemanage(0);
    setScheduleFileCreation(false); setFileCreationDateTime("");
  };

  const buildFilterPayload = () => {
    const jsonData = selectedFields
      .map((field, i) => ({
        fieldName: field,
        searchType: operations[i],
        value: ["before", "after"].includes(operations[i]) && values[i] !== ""
          ? Number(values[i])
          : values[i],
        logicalOperator: logicalOperators[i] || "AND",
      }))
      .filter((item) => item.fieldName && item.searchType && item.value !== "");
    return { requiredData: chips.join(","), jsonData };
  };

  const toggleDateSelection = (date) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const handleServiceChange = (e) => {
    const nextService = e.target.value;
    setServiceType(nextService);
    setSelectedFlow("");
    setSelectedTemplate("");
    setSelectedRcsCategory("");
    setSelectedRcsBotId("");
    setSelectedWhatsAppPhoneId("");
    if (nextService !== "sms") {
      setSelectedEntityId("");
      setSelectedEntity(null);
    }
  };

  const handleRcsCategoryChange = (e) => {
    setSelectedRcsCategory(e.target.value);
    setSelectedRcsBotId("");
    setSelectedTemplate("");
  };

  const handleRcsBotChange = (e) => {
    setSelectedRcsBotId(e.target.value);
    setSelectedTemplate("");
  };

  const handleEntityChange = (e) => {
    const entity = principalEntities.find(
      (item) => String(item.principalentity_id) === String(e.target.value),
    );
    setSelectedEntityId(entity?.principalentity_id || "");
    setSelectedEntity(entity || null);
    setSelectedTemplate("");
  };

  const getSelectedTemplateName = () => {
    if (serviceType === "whatsapp") {
      return waTemplates.find((t) => t.template_id === selectedTemplate)?.name;
    }
    if (serviceType === "rcs") {
      return selectedTemplate;
    }
    if (serviceType === "sms") {
      return smsTemplates.find((t) => t.template_id === selectedTemplate)?.template_name;
    }
    return undefined;
  };

  const handleSaveEvent = async () => {
    if (!campaignType) {
      toast.error("Please select a campaign type.");
      return;
    }
    if (!eventName.trim()) {
      toast.error("Please enter an event name.");
      return;
    }
    if (chips.length === 0) {
      toast.error("Please select at least one field for the event data filter.");
      return;
    }

    const { requiredData, jsonData } = buildFilterPayload();
    if (jsonData.length === 0) {
      toast.error("Please add at least one valid filter condition.");
      return;
    }

    if (!serviceType) {
      toast.error("Please select a service (Flow / SMS / RCS / Whatsapp).");
      return;
    }
    if (serviceType === "flow" && !selectedFlow) {
      toast.error("Please select a flow.");
      return;
    }
    if (serviceType === "sms" && !selectedEntityId) {
      toast.error("Please select an Entity Id for SMS.");
      return;
    }
    if (serviceType === "whatsapp" && !selectedWhatsAppPhoneId) {
      toast.error("Please select a WABA number for WhatsApp.");
      return;
    }
    if (serviceType === "rcs" && !selectedRcsBotId) {
      toast.error("Please select an RCS bot.");
      return;
    }
    if (serviceType !== "flow" && !selectedTemplate) {
      toast.error("Please select a template.");
      return;
    }
    if (campaignType !== "realtime") {
      if (!scheduleDateTime) {
        toast.error(
          campaignType === "once" ? "Please select the run date & time." : "Please select the next run date & time."
        );
        return;
      }
      if (!isFutureSchedule(scheduleDateTime)) {
        toast.error(
          campaignType === "once"
            ? "The run date and time must be in the future."
            : "The run time must be later than the current time."
        );
        return;
      }
      if (scheduleFileCreation && !fileCreationDateTime) {
        toast.error("Please select the file creation date & time.");
        return;
      }
      if (scheduleFileCreation && !isFutureSchedule(fileCreationDateTime)) {
        toast.error("The file creation date and time must be in the future.");
        return;
      }
      if (campaignType === "repeat" && !runDuration) {
        toast.error("Please select a repeat period.");
        return;
      }
      if (campaignType === "repeat" && runDuration === "weekly" && selectedDays.length === 0) {
        toast.error("Please select at least one day.");
        return;
      }
      if (campaignType === "repeat" && runDuration === "monthly" && selectedDates.length === 0) {
        toast.error("Please select at least one date.");
        return;
      }
      if (filemanage === 1 && !batchLimit) {
        toast.error("Please enter the file limit.");
        return;
      }
      if (filemanage === 2 && (!recordsPerPage || !batchLimit)) {
        toast.error("Please enter records per file and the file limit.");
        return;
      }
    }

    const msgType = serviceType.charAt(0) + serviceType.slice(1).toLowerCase();
    const msgTemplate =
      serviceType === "flow"
        ? fetchedFlows.find((f) => f.flow_id === selectedFlow)?.flow_name || ""
        : getSelectedTemplateName() || "";

    const basePayload = {
      ...(editingEventId ? { event_id: editingEventId } : {}),
      event_name: eventName.trim(),
      msg_type: msgType,
      msg_template: msgTemplate,
      ...(serviceType === "whatsapp"
        ? {
            phone_number_id: selectedWhatsAppPhoneId,
            waba_id: selectedWhatsAppWabaId,
          }
        : {}),
      ...(serviceType === "rcs"
        ? {
            bot_id: selectedRcsBotId,
            agent_name: selectedRcsBot?.agent_name || "",
          }
        : {}),
      ...(serviceType === "sms" && selectedEntity?.pe_id
        ? { pe_id: selectedEntity.pe_id }
        : {}),
      filter_data_jsonb: { jsonData, requiredData },
      is_active: true,
      further_split: furtherSplit,
      further_split_noofrecords: furtherSplit ? Number(furtherSplitNoOfRecords) || 0 : 0,
      further_split_time_gap: furtherSplit ? Number(furtherSplitTimeGap) || 0 : 0,
      createdat: scheduleFileCreation
        ? buildISTDateTime(fileCreationDateTime)
        : nowISTString(),
    };

    const effectiveRecordsPerPage = filemanage === 2 ? Number(recordsPerPage) || 0 : 0;
    const effectiveBatchLimit = filemanage === 1 || filemanage === 2 ? Number(batchLimit) || 0 : 0;

    const payload =
      campaignType === "realtime"
        ? {
            ...basePayload,
            repeat_type: "realtime",
            once_datetime: null,
            repeat_period: null,
            custom_period: null,
            periods_values: [],
            records_per_page: 0,
            batch_limit: 0,
          }
        : campaignType === "once"
        ? {
            ...basePayload,
            repeat_type: "once",
            once_datetime: buildISTDateTime(scheduleDateTime),
            repeat_period: "custom",
            custom_period: null,
            periods_values: [],
            records_per_page: effectiveRecordsPerPage,
            batch_limit: effectiveBatchLimit,
          }
        : {
            ...basePayload,
            repeat_type: "repeat",
            once_datetime: null,
            repeat_period: runDuration,
            custom_period: runDuration === "weekly" ? "weekdays" : null,
            periods_values:
              runDuration === "weekly"
                ? selectedDays.map((d) => d.toLowerCase())
                : runDuration === "monthly"
                ? selectedDates
                : [],
            run_at_time: extractRunAtTime(scheduleDateTime),
            records_per_page: effectiveRecordsPerPage,
            batch_limit: effectiveBatchLimit,
          };

    try {
      await createEventFlow(payload).unwrap();
      toast.success(editingEventId ? "Event updated successfully!" : "Event created successfully!");
      resetEventForm();
      setShowForm(false);
      refetchEvents();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to save event. Please try again.");
    }
  };

  const handleEditEvent = (item) => {
    resetEventForm();
    setEditingEventId(item.event_id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEventStatusChange = async (item, isActive) => {
    try {
      const response = await setActiveInactiveEvent({
        event_id: item.event_id,
        repeat_type: "repeat",
        is_active: isActive,
      }).unwrap();
      toast.success(response?.message || "Event status updated successfully");
      refetchEvents();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to update event status");
    }
  };

  const handleDeleteClick = (eventId) => {
    setDeleteTargetId(eventId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteTargetId) return;
    toast.success("Event deleted successfully!");
    setOpenDeleteDialog(false);
    setDeleteTargetId(null);
    refetchEvents();
  };

  const deleteTargetEvent = eventsList.find((e) => e.event_id === deleteTargetId);

  const renderWhatsAppPhonePreview = () => {
    const tpl = waTemplateData?.templates?.find((t) => t.template_id === selectedTemplate);
    const components = tpl?.components || [];
    const carouselComp = components.find((c) => c.type === "CAROUSEL");
    const buttonComp = components.find((c) => c.type === "BUTTONS");
    const hasOTP = buttonComp?.buttons?.some((b) => b.type === "OTP");

    return (
      <Box sx={{
        width: 300,
        display: "flex", flexDirection: "column",
        bgcolor: "#000", borderRadius: "36px",
        boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
        overflow: "hidden", border: "6px solid #222",
      }}>
        <Box sx={{ height: 28, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#075E54" }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, color: "#fff" }}>{dayjs().format("HH:mm")}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: "#fff" }}>
            <WifiIcon sx={{ fontSize: 13 }} />
            <NetworkCellIcon sx={{ fontSize: 13 }} />
            <BatteryFullIcon sx={{ fontSize: 13 }} />
          </Stack>
        </Box>

        <Box sx={{ bgcolor: "#075E54", px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#128C7E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}>
              {tpl?.name?.[0]?.toUpperCase() ?? "W"}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {tpl?.name ?? "Template"}
            </Typography>
            <Typography sx={{ fontSize: "0.59rem", color: "rgba(255,255,255,0.65)" }}>
              {tpl?.category} • {tpl?.language}
            </Typography>
          </Box>
          {tpl?.status && (() => {
            const { bg, color } = statusStyle(tpl.status);
            return (
              <Box sx={{ px: 0.9, py: 0.2, borderRadius: "8px", bgcolor: bg, flexShrink: 0 }}>
                <Typography sx={{ fontSize: "0.57rem", fontWeight: 700, color }}>{tpl.status}</Typography>
              </Box>
            );
          })()}
        </Box>

        <Box sx={{
          flexGrow: 1, overflowY: "auto",
          px: 1, pt: 1.5, pb: 2,
          minHeight: 360, maxHeight: "62vh",
          bgcolor: "#f5f5f5",
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#bbb", borderRadius: "2px" },
        }}>
          {tpl ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{
                  fontSize: "0.6rem", color: "#555",
                  bgcolor: "rgba(255,255,255,0.75)", px: 1.5, py: 0.3, borderRadius: "8px",
                  display: "inline-block",
                }}>
                  Today
                </Typography>
              </Box>

              <Box sx={{ width: "100%" }}>
                {carouselComp ? (
                  <>
                    <CarouselPreviewGray cards={carouselComp.cards || []} />
                    {components.filter((c) => c.type === "BODY").map((bodyComp, idx) => (
                      <Box key={idx} sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                        <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                          {bodyComp.text}
                        </Typography>
                      </Box>
                    ))}
                  </>
                ) : hasOTP ? (
                  (() => {
                    const bodyComp = components.find((c) => c.type === "BODY");
                    const footerComp = components.find((c) => c.type === "FOOTER");
                    const headerComp = components.find((c) => c.type === "HEADER");

                    return (
                      <>
                        <Box sx={{
                          bgcolor: "#fff",
                          borderRadius: "8px",
                          p: 1.5,
                          mb: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          borderLeft: "4px solid #d32f2f"
                        }}>
                          <LockIcon sx={{ fontSize: 16, color: "#d32f2f", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#d32f2f" }}>
                              Security verification
                            </Typography>
                            <Typography sx={{ fontSize: "0.65rem", color: "#666", mt: 0.3 }}>
                              This is an authentication template
                            </Typography>
                          </Box>
                        </Box>

                        {headerComp?.text && (
                          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#000" }}>
                              {headerComp.text}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                          {bodyComp?.text ? (
                            <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                              {bodyComp.text}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: "0.8rem", color: "#333" }}>
                              Your verification code is: <strong>@x!yz</strong>
                            </Typography>
                          )}
                        </Box>

                        {footerComp?.code_expiration_minutes && (
                          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                            <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>
                              Code expires in {footerComp.code_expiration_minutes} minute{footerComp.code_expiration_minutes > 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        )}

                        {buttonComp?.buttons?.length > 0 && (
                          <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                            <Stack spacing={1}>
                              {buttonComp.buttons.map((btn, bIdx) => (
                                <Tooltip
                                  key={bIdx}
                                  title={getButtonTooltip(btn)}
                                  arrow
                                  disableHoverListener={!getButtonTooltip(btn)}
                                >
                                  <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      borderRadius: "6px",
                                      bgcolor: "#075E54",
                                      color: "#fff",
                                      py: 1,
                                      "&:hover": { bgcolor: "#128C7E" }
                                    }}
                                  >
                                    <LockIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                    {getOTPButtonLabel(btn.otp_type)}
                                  </Button>
                                </Tooltip>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <>
                    {components.some((c) => c.type === "HEADER") && (
                      <Box sx={{ bgcolor: "#fff", borderRadius: "8px", overflow: "hidden", mb: 1 }}>
                        {components
                          .filter((c) => c.type === "HEADER")
                          .map((headerComp, idx) => {
                            const mediaUrl = headerComp?.example?.header_handle?.[0] ?? null;
                            const mediaFormat = headerComp.format ?? "TEXT";

                            return (
                              <Box key={idx}>
                                {mediaFormat === "IMAGE" && (
                                  <HeaderMedia url={mediaUrl} format="IMAGE" height={110} />
                                )}
                                {mediaFormat === "VIDEO" && (
                                  <HeaderMedia url={mediaUrl} format="VIDEO" height={140} />
                                )}
                                {mediaFormat === "DOCUMENT" && (
                                  <Box sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    p: 1.5, bgcolor: "#fafafa",
                                  }}>
                                    <Box sx={{
                                      width: 36, height: 36, borderRadius: "6px",
                                      bgcolor: "#e53935", display: "flex",
                                      alignItems: "center", justifyContent: "center", flexShrink: 0,
                                    }}>
                                      <Typography sx={{ fontSize: "0.55rem", fontWeight: 700, color: "#fff" }}>PDF</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: "0.72rem", color: "#333" }}>
                                      {mediaUrl ? "Document attached" : "No document"}
                                    </Typography>
                                  </Box>
                                )}
                                {headerComp.text && mediaFormat === "TEXT" && (
                                  <Typography sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#000", p: 1 }}>
                                    {headerComp.text}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    )}

                    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                      {components.filter((c) => c.type === "BODY").map((bodyComp, idx) => (
                        <Typography key={idx} sx={{ fontSize: "0.8rem", color: "#333" }}>
                          {bodyComp.text}
                        </Typography>
                      ))}
                      {components.filter((c) => c.type === "FOOTER").map((footerComp, idx) => (
                        <Typography key={idx} sx={{ fontSize: "0.7rem", color: "#999", mt: 0.5 }}>
                          {footerComp.text}
                        </Typography>
                      ))}
                    </Box>

                    {components.some((c) => c.type === "BUTTONS") && (
                      <Box sx={{ bgcolor: "#fff", borderRadius: "8px", p: 1.5, mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem", mb: 0.5, display: "block" }}>
                          Suggested Actions
                        </Typography>
                        <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                          {components
                            .filter((c) => c.type === "BUTTONS")
                            .flatMap((b) => b.buttons)
                            .map((btn, bIdx) => (
                              <Tooltip
                                key={bIdx}
                                title={getButtonTooltip(btn)}
                                arrow
                                disableHoverListener={!getButtonTooltip(btn)}
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    fontSize: "0.65rem",
                                    textTransform: "none",
                                    borderRadius: "6px",
                                    ...(bIdx === 0
                                      ? { bgcolor: "#000", color: "#fff" }
                                      : { bgcolor: "#f0f0f0", color: "#333" }),
                                  }}
                                >
                                  {btn.text}
                                </Button>
                              </Tooltip>
                            ))}
                        </Stack>
                      </Box>
                    )}
                  </>
                )}

                <Typography sx={{ fontSize: "0.57rem", color: "#888", textAlign: "right", mt: 0.4 }}>
                  {dayjs().format("HH:mm")} ✓✓
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
              <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>No template selected</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderRcsPhonePreview = () => {
    const tpl = rcsEventTemplate;
    const cards = tpl?.carousel_cards || [];
    const isCarousel = cards.length > 0;
    const isRichCard = !isCarousel && !!tpl?.rich_card_image;
    const selectedCard = cards[selectedCardIndex] || null;
    const buttonSource = [
      selectedCard?.buttonInformation,
      selectedCard?.buttons,
      tpl?.buttonInformation,
      tpl?.buttons,
    ].find((source) => Array.isArray(source) && source.length) || [];
    const buttons = buttonSource
      .map((button) =>
        typeof button === "string"
          ? { suggestion_text: button }
          : {
              ...button,
              suggestion_text:
                button?.suggestion_text ||
                button?.suggestionText ||
                button?.text ||
                button?.label ||
                "",
            },
      )
      .filter((button) => button.suggestion_text);
    const replyButtons = buttons.filter(
      (btn) =>
        Number(btn.button_action_id) === 1 ||
        (!btn.button_action_id && !btn.url && !btn.phone_number),
    );
    const actionButtons = buttons.filter((btn) => !replyButtons.includes(btn));

    return (
      <Box
        sx={{
          width: "300px",
          minHeight: "400px",
          maxHeight: "75vh",
          backgroundColor: "#fff",
          border: "8px solid #000",
          borderRadius: "32px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 15px 50px rgba(0,0,0,0.3)",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "30px",
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#000",
            color: "#fff",
          }}
        >
          <Typography sx={{ fontSize: "0.75rem" }}>{dayjs().format("HH:mm")}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <WifiIcon sx={{ fontSize: "16px" }} />
            <NetworkCellIcon sx={{ fontSize: "16px" }} />
            <BatteryFullIcon sx={{ fontSize: "16px" }} />
          </Stack>
        </Box>

        <Box
          sx={{
            p: 2,
            overflowY: "auto",
            flexGrow: 1,
            backgroundColor: "#f5f5f5",
            minHeight: "300px",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#ccc", borderRadius: "2px", "&:hover": { bgcolor: "#999" } },
          }}
        >
          {rcsEventTemplateLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} sx={{ color: BRAND }} />
            </Box>
          ) : tpl ? (
            <Stack spacing={2}>
              <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 2, borderBottom: "1px solid #ccc" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem", color: "#000" }}>
                  {tpl.template_name}
                </Typography>
                {tpl.template_type && (
                  <Box sx={{
                    display: "inline-block", backgroundColor: "#f0f0f0", px: 1, py: 0.3,
                    borderRadius: "4px", fontSize: "0.65rem", fontWeight: 600, color: "#333",
                  }}>
                    {tpl.template_type}
                  </Box>
                )}
              </Box>

              <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 2, borderBottom: "1px solid #ccc" }}>
                {isCarousel ? (
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      {cards.map((_, idx) => (
                        <Button
                          key={idx}
                          onClick={() => setSelectedCardIndex(idx)}
                          sx={{
                            minWidth: "32px", height: "32px", borderRadius: "4px",
                            fontSize: "0.75rem", fontWeight: 600, transition: "all 0.3s ease",
                            ...(selectedCardIndex === idx
                              ? { backgroundColor: "#000", color: "#fff" }
                              : { backgroundColor: "#e0e0e0", color: "#333", "&:hover": { backgroundColor: "#ccc" } }),
                          }}
                        >
                          {idx + 1}
                        </Button>
                      ))}
                    </Box>

                    {selectedCard ? (
                      <Stack spacing={1}>
                        <Box sx={{ borderRadius: "8px", overflow: "hidden" }}>
                          {isVideoUrl(selectedCard.carousel_image) ? (
                            <video controls style={{ width: "100%", borderRadius: "8px", display: "block" }}>
                              <source src={selectedCard.carousel_image} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <img
                              src={selectedCard.carousel_image}
                              alt={`Card ${selectedCard.card_no}`}
                              style={{ width: "100%", borderRadius: "8px", display: "block" }}
                            />
                          )}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "#000" }}>
                            {selectedCard.carousel_title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.5 }}>
                            {selectedCard.carousel_description}
                          </Typography>
                        </Box>
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">No card data available.</Typography>
                    )}
                  </Stack>
                ) : isRichCard ? (
                  <Stack spacing={1.5}>
                    <Box sx={{ borderRadius: "8px", overflow: "hidden", backgroundColor: "#f0f0f0" }}>
                      {isVideoUrl(tpl.rich_card_image) ? (
                        <video controls style={{ width: "100%", borderRadius: "8px", display: "block" }}>
                          <source src={tpl.rich_card_image} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <img
                          src={tpl.rich_card_image}
                          alt="Rich Card"
                          style={{ width: "100%", borderRadius: "8px", display: "block" }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "#000" }}>
                        {tpl.rich_card_title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.5 }}>
                        {tpl.rich_card_description}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#333", lineHeight: 1.6, fontSize: "0.9rem" }}>
                    {tpl.smstext || "No content available"}
                  </Typography>
                )}
              </Box>

              {buttons.length > 0 && (
                <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 2, borderBottom: "1px solid #ccc" }}>
                  <Stack spacing={1.5}>
                    {replyButtons.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem" }}>
                          Suggested Replies
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                          {replyButtons.map((btn, idx) => {
                            const tooltipTitle = getButtonTooltip(btn);
                            return (
                              <Tooltip key={`reply-${idx}`} title={tooltipTitle} disableHoverListener={!tooltipTitle}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  sx={{
                                    borderRadius: "6px", textTransform: "none", fontWeight: 600, fontSize: "0.75rem",
                                    border: "1px solid #ccc", color: "#333", transition: "all 0.3s ease",
                                    "&:hover": { backgroundColor: "#f0f0f0", borderColor: "#999" },
                                  }}
                                >
                                  {btn.suggestion_text}
                                </Button>
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}

                    {actionButtons.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#666", textTransform: "uppercase", fontSize: "0.65rem" }}>
                          Suggested Actions
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
                          {actionButtons.map((btn, idx) => {
                            const tooltipTitle = getButtonTooltip(btn);
                            return (
                              <Tooltip key={`action-${idx}`} title={tooltipTitle} disableHoverListener={!tooltipTitle}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    borderRadius: "6px", textTransform: "none", fontWeight: 600, fontSize: "0.75rem",
                                    backgroundColor: "#000", color: "#fff", transition: "all 0.3s ease",
                                    "&:hover": { backgroundColor: "#333" },
                                  }}
                                >
                                  {btn.suggestion_text}
                                </Button>
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Stack>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
              <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>No template selected</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderSmsPhonePreview = () => {
    const tpl = smsTemplates.find((t) => t.template_id === selectedTemplate);

    return (
      <Box
        sx={{
          width: "300px",
          minHeight: "400px",
          maxHeight: "75vh",
          backgroundColor: "#fff",
          border: "8px solid #000",
          borderRadius: "32px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 15px 50px rgba(0,0,0,0.3)",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "30px",
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#000",
            color: "#fff",
          }}
        >
          <Typography sx={{ fontSize: "0.75rem" }}>{dayjs().format("HH:mm")}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <WifiIcon sx={{ fontSize: "16px" }} />
            <NetworkCellIcon sx={{ fontSize: "16px" }} />
            <BatteryFullIcon sx={{ fontSize: "16px" }} />
          </Stack>
        </Box>

        <Box sx={{ bgcolor: "#f5f5f5", px: 1.5, py: 1.25, display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #e5e5e5" }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#0288d1", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}>
              {(tpl?.header_name || "SMS")[0]?.toUpperCase()}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {tpl?.header_name || "SMS Sender"}
            </Typography>
            {tpl?.trai_template_id && (
              <Typography sx={{ fontSize: "0.6rem", color: "#888" }}>
                Template ID: {tpl.trai_template_id}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            overflowY: "auto",
            flexGrow: 1,
            backgroundColor: "#f5f5f5",
            minHeight: "300px",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": { bgcolor: "#ccc", borderRadius: "2px" },
          }}
        >
          {tpl ? (
            <Stack spacing={2}>
              <Box sx={{ backgroundColor: "#fff", borderRadius: "8px", p: 2, borderBottom: "1px solid #ccc" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem", color: "#000" }}>
                  {tpl.template_name}
                </Typography>
                {tpl.template_type && (
                  <Box sx={{
                    display: "inline-block", backgroundColor: "#f0f0f0", px: 1, py: 0.3,
                    borderRadius: "4px", fontSize: "0.65rem", fontWeight: 600, color: "#333",
                  }}>
                    {tpl.template_type}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Box sx={{
                  maxWidth: "85%",
                  bgcolor: "#fff",
                  borderRadius: "12px",
                  borderTopLeftRadius: "4px",
                  p: 1.5,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                }}>
                  <Typography sx={{ fontSize: "0.85rem", color: "#333", whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
                    {tpl.template_text || "No content available"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.6rem", color: "#999", textAlign: "right", mt: 0.5 }}>
                    {dayjs().format("HH:mm")}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
              <Typography sx={{ color: "#aaa", fontSize: "0.75rem" }}>No template selected</Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderEventDataFilter = () => (
    <Box sx={sectionSx}>
      <Typography sx={sectionLabelSx}>
        <TuneRoundedIcon sx={{ fontSize: "1rem" }} /> Event Data Filter
      </Typography>
      <Box
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: 0.5,
          p: 1.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 3,
          mt: 1,
          background: "rgba(255,255,255,0.5)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Box sx={{ flex: 2, display: "flex", flexDirection: "row", gap: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1.25, fontSize: "0.85rem", fontWeight: 600 }}>
              Add Fields
            </Typography>
            <FormControl size="small">
              <Select value={selected} onChange={handleFilterFieldSelect} displayEmpty sx={smallSelectSx}>
                <MenuItem value="" disabled sx={{ fontSize: "0.8rem" }}>Select option</MenuItem>
                {fieldList.map((name) => (
                  <MenuItem key={name} value={name} disabled={chips.includes(name)} sx={{ fontSize: "0.8rem" }}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: "0.85rem", fontWeight: 600 }}>
              Selected Fields Sequence
            </Typography>
            <DragDropContext onDragEnd={onFilterDragEnd}>
              <Droppable droppableId="eventFilterChips" direction="horizontal">
                {(provided) => (
                  <Stack direction="row" spacing={1} sx={chipAreaSx} ref={provided.innerRef} {...provided.droppableProps}>
                    {chips.map((chip, index) => (
                      <Draggable key={chip} draggableId={chip} index={index}>
                        {(provided) => (
                          <Chip
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            label={chip}
                            onDelete={() => handleFilterChipDelete(chip)}
                            variant="outlined"
                            sx={{ borderRadius: "10px", background: "rgba(255,255,255,0.85)", backdropFilter: "blur(4px)", fontSize: "0.78rem", fontWeight: 500 }}
                          />
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ borderRadius: 1, overflow: "hidden", mt: 1.5 }}>
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: "#0288d1" }}>
              <TableRow>
                <TableCell sx={thCell}>Join</TableCell>
                <TableCell sx={thCell}>Fields</TableCell>
                <TableCell sx={thCell}>Operation</TableCell>
                <TableCell sx={{ ...thCell, borderRight: "none" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Value
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddRow}
                      disabled={selectedFields.length >= fieldList.length}
                      endIcon={<AddIcon />}
                      sx={{
                        fontSize: "0.7rem",
                        textTransform: "none",
                        color: "white",
                        borderColor: "rgba(255,255,255,0.6)",
                        "&:hover": { borderColor: "white", background: "rgba(255,255,255,0.1)" },
                      }}
                    >
                      Add Filter
                    </Button>
                  </Box>
                </TableCell>
                <TableCell sx={{ ...thCell, borderRight: "none" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ background: "rgba(255,255,255,0.23)" }}>
              {selectedFields.map((field, index) => (
                <TableRow key={index} hover sx={{ "& td": { fontSize: "0.8rem", py: 0.5 }, "&:hover": { background: "rgba(186,230,253,0.2)" } }}>
                  <TableCell sx={tdCell}>
                    {index === 0 ? (
                      <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", textAlign: "center" }}>
                        -
                      </Typography>
                    ) : (
                      <FormControl fullWidth size="small" sx={{ minWidth: 90 }}>
                        <Select
                          value={logicalOperators[index] || "AND"}
                          onChange={(e) => handleLogicalOperatorChange(index, e.target.value)}
                          sx={smallSelectSx}
                          inputProps={{ "aria-label": `Logical operator before filter ${index + 1}` }}
                        >
                          <MenuItem value="AND" sx={{ fontSize: "0.8rem" }}>AND</MenuItem>
                          <MenuItem value="OR" sx={{ fontSize: "0.8rem" }}>OR</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </TableCell>
                  <TableCell sx={tdCell}>
                    <FormControl fullWidth size="small" sx={{ minWidth: 150 }}>
                      <InputLabel id={`efield-label-${index}`} sx={{ fontSize: "0.8rem" }}>Field</InputLabel>
                      <Select
                        labelId={`efield-label-${index}`}
                        value={field}
                        label="Field"
                        onChange={(e) => handleFieldChange(index, e.target.value)}
                        sx={smallSelectSx}
                      >
                        {columns.map((col) => (
                          <MenuItem
                            key={col.column_name}
                            value={col.column_name}
                            disabled={selectedFields.some((f, i) => f === col.column_name && i !== index)}
                            sx={{ fontSize: "0.8rem" }}
                          >
                            {col.column_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={tdCell}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`eop-label-${index}`} sx={{ fontSize: "0.8rem" }}>Operation</InputLabel>
                      <Select
                        labelId={`eop-label-${index}`}
                        value={operations[index] || ""}
                        label="Operation"
                        onChange={(e) => handleOperationChange(index, e.target.value)}
                        sx={smallSelectSx}
                      >
                        {OPERATION_OPTIONS.map((op) => (
                          <MenuItem key={op} value={op} sx={{ fontSize: "0.8rem" }}>
                            {op.charAt(0).toUpperCase() + op.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ ...tdCell, borderRight: "0.5px solid #d0d0d0" }}>
                    {["before", "after"].includes(operations[index]) ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={values[index] || ""}
                          displayEmpty
                          onChange={(e) => handleValueChange(index, e.target.value)}
                          sx={smallSelectSx}
                          inputProps={{ "aria-label": `Days ${operations[index]} date` }}
                        >
                          <MenuItem value="" disabled>Select days</MenuItem>
                          {Array.from({ length: 30 }, (_, dayIndex) => dayIndex + 1).map((day) => (
                            <MenuItem key={day} value={day} sx={{ fontSize: "0.8rem" }}>{day}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : operations[index] === "between" ? (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {isDateLike(dataTypes[index]) ? (
                          <>
                            <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                              value={values[index]?.split("|")[0] || ""}
                              onChange={(e) => handleValueChange(index, `${e.target.value}|${values[index]?.split("|")[1] || ""}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                            <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                              value={values[index]?.split("|")[1] || ""}
                              onChange={(e) => handleValueChange(index, `${values[index]?.split("|")[0] || ""}|${e.target.value}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                          </>
                        ) : (
                          <>
                            <TextField size="small" placeholder="From"
                              value={values[index]?.split("|")[0] || ""}
                              onChange={(e) => handleValueChange(index, `${e.target.value}|${values[index]?.split("|")[1] || ""}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                            <TextField size="small" placeholder="To"
                              value={values[index]?.split("|")[1] || ""}
                              onChange={(e) => handleValueChange(index, `${values[index]?.split("|")[0] || ""}|${e.target.value}`)}
                              sx={{ "& .MuiOutlinedInput-root": glassInput }}
                            />
                          </>
                        )}
                      </Box>
                    ) : isDateLike(dataTypes[index]) ? (
                      <TextField size="small" fullWidth type="datetime-local" InputLabelProps={{ shrink: true }}
                        value={values[index] || ""}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": glassInput }}
                      />
                    ) : (
                      <TextField size="small" fullWidth placeholder="Enter value"
                        value={values[index] || ""}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        sx={{ "& .MuiOutlinedInput-root": glassInput }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...tdCell, borderRight: "none" }} align="center">
                    {selectedFields.length > 1 && (
                      <Button color="error" size="small" onClick={() => handleRemoveRow(index)} sx={{ fontSize: "0.72rem", textTransform: "none" }}>
                        Remove
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );

  const renderFlowConfiguration = () => (
    <Box sx={sectionSx}>
      <Typography sx={sectionLabelSx}>
        <ForumRoundedIcon sx={{ fontSize: "1rem" }} /> Destination & Schedule
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={3}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ fontSize: "0.75rem" }}>Service</InputLabel>
            <Select label="Service" value={serviceType} onChange={handleServiceChange} sx={smallSelectSx}>
              {SERVICE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.78rem" }}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {serviceType === "flow" && (
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: "0.75rem" }}>Flow Name</InputLabel>
              <Select label="Flow Name" value={selectedFlow} onChange={(e) => setSelectedFlow(e.target.value)} sx={smallSelectSx}>
                {fetchedFlows.map((f) => (
                  <MenuItem key={f.flow_id} value={f.flow_id} sx={{ fontSize: "0.78rem" }}>{f.flow_name}</MenuItem>
                ))}
                {/* ✅ Fallback: show saved flow name while list loads */}
                {selectedFlow &&
                  !fetchedFlows.some((f) => f.flow_id === selectedFlow) && (
                    <MenuItem value={selectedFlow} sx={{ fontSize: "0.78rem", fontStyle: "italic" }}>
                      {prefilleddata?.data?.msg_template || selectedFlow}
                    </MenuItem>
                  )}
              </Select>
            </FormControl>
          </Grid>
        )}

        {serviceType === "sms" && (
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: "0.75rem" }}>Entity Id</InputLabel>
              <Select
                label="Entity Id"
                value={selectedEntityId}
                onChange={handleEntityChange}
                sx={smallSelectSx}
              >
                <MenuItem value="" sx={{ fontSize: "0.78rem" }}>
                  Select Entity Id
                </MenuItem>
                {principalEntitiesLoading ? (
                  <MenuItem disabled sx={{ fontSize: "0.78rem" }}>
                    Loading...
                  </MenuItem>
                ) : principalEntitiesError ? (
                  <MenuItem disabled sx={{ fontSize: "0.78rem" }}>
                    Unable to load entities
                  </MenuItem>
                ) : (
                  principalEntities.map((entity) => (
                    <MenuItem
                      key={entity.principalentity_id}
                      value={entity.principalentity_id}
                      sx={{ fontSize: "0.78rem" }}
                    >
                      {entity.pe_name} ({maskNumber(entity.pe_id)})
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        )}

        {serviceType === "whatsapp" && (
          <Grid item xs={12} sm={3}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: "0.75rem" }}>WABA Number</InputLabel>
              <Select
                label="WABA Number"
                value={selectedWhatsAppPhoneId}
                onChange={(e) => {
                  setSelectedWhatsAppPhoneId(e.target.value);
                  setSelectedTemplate("");
                }}
                sx={smallSelectSx}
              >
                {whatsAppPhoneLoading ? (
                  <MenuItem disabled sx={{ fontSize: "0.78rem" }}>Loading numbers...</MenuItem>
                ) : whatsAppPhoneError ? (
                  <MenuItem disabled sx={{ fontSize: "0.78rem" }}>Unable to load numbers</MenuItem>
                ) : whatsAppPhoneNumbers.length === 0 ? (
                  <MenuItem disabled sx={{ fontSize: "0.78rem" }}>No WhatsApp numbers available</MenuItem>
                ) : (
                  whatsAppPhoneNumbers.map((phone) => (
                    <MenuItem key={phone.id || phone.phone_number_id} value={phone.phone_number_id} sx={{ fontSize: "0.78rem" }}>
                      {phone.phone_number}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        )}

        {serviceType === "rcs" && (
          <>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontSize: "0.75rem" }}>RCS Category</InputLabel>
                <Select
                  label="RCS Category"
                  value={selectedRcsCategory}
                  onChange={handleRcsCategoryChange}
                  sx={smallSelectSx}
                >
                  {rcsCategoriesLoading ? (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>Loading categories...</MenuItem>
                  ) : rcsCategories.length === 0 ? (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>No RCS categories available</MenuItem>
                  ) : (
                    rcsCategories.map((category) => (
                      <MenuItem
                        key={category.rcs_bot_type_id}
                        value={String(category.rcs_bot_type_id)}
                        sx={{ fontSize: "0.78rem" }}
                      >
                        {category.bot_type_name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontSize: "0.75rem" }}>RCS Bot</InputLabel>
                <Select
                  label="RCS Bot"
                  value={selectedRcsBotId}
                  onChange={handleRcsBotChange}
                  disabled={!selectedRcsCategory}
                  sx={smallSelectSx}
                >
                  {!selectedRcsCategory ? (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>Select a category first</MenuItem>
                  ) : rcsBotsLoading ? (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>Loading bots...</MenuItem>
                  ) : rcsBots.length === 0 ? (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>No bots in this category</MenuItem>
                  ) : (
                    rcsBots.map((bot) => (
                      <MenuItem
                        key={bot.rcs_customer_bot_id || bot.bot_id}
                        value={String(bot.bot_id)}
                        sx={{ fontSize: "0.78rem" }}
                      >
                        {bot.agent_name}
                      </MenuItem>
                    ))
                  )}
                  {selectedRcsBotId && !rcsBots.some((bot) => String(bot.bot_id) === String(selectedRcsBotId)) && (
                    <MenuItem value={String(selectedRcsBotId)} sx={{ fontSize: "0.78rem", fontStyle: "italic" }}>
                      {prefilleddata?.data?.agent_name || selectedRcsBotId}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          </>
        )}

        {serviceType && serviceType !== "flow" && (
          <Grid item xs={12} sm={3}>
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                <InputLabel sx={{ fontSize: "0.75rem" }}>Template</InputLabel>
                <Select
                  label="Template"
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  disabled={
                    (serviceType === "whatsapp" && !selectedWhatsAppWabaId) ||
                    (serviceType === "rcs" && !selectedRcsBotId)
                  }
                  sx={smallSelectSx}
                >
                  {serviceType === "whatsapp" && waTemplatesLoading && (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>Loading templates...</MenuItem>
                  )}
                  {serviceType === "whatsapp" && selectedWhatsAppWabaId && !waTemplatesLoading && waTemplates.length === 0 && (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>No approved templates for this number</MenuItem>
                  )}
                  {serviceType === "whatsapp" && waTemplates.map((t) => (
                    <MenuItem key={t.template_id} value={t.template_id} sx={{ fontSize: "0.78rem" }}>{t.name}</MenuItem>
                  ))}
                  {serviceType === "rcs" && rcsTemplatesLoading && (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>Loading bot templates...</MenuItem>
                  )}
                  {serviceType === "rcs" && selectedRcsBotId && !rcsTemplatesLoading && rcsTemplates.length === 0 && (
                    <MenuItem disabled sx={{ fontSize: "0.78rem" }}>No approved templates for this bot</MenuItem>
                  )}
                  {serviceType === "rcs" && rcsTemplates.map((t) => (
                    <MenuItem key={t.id} value={t.name} sx={{ fontSize: "0.78rem" }}>{t.name}</MenuItem>
                  ))}
                  {serviceType === "sms" && smsTemplates.map((t) => (
                    <MenuItem key={t.template_id} value={t.template_id} sx={{ fontSize: "0.78rem" }}>{t.template_name}</MenuItem>
                  ))}

                  {/* ✅ Fallback: show saved template name until the id resolves */}
                  {selectedTemplate &&
                    !(
                      (serviceType === "whatsapp" && waTemplates.some((t) => t.template_id === selectedTemplate)) ||
                      (serviceType === "sms" && smsTemplates.some((t) => t.template_id === selectedTemplate)) ||
                      (serviceType === "rcs" && rcsTemplates.some((t) => t.name === selectedTemplate))
                    ) && (
                      <MenuItem value={selectedTemplate} sx={{ fontSize: "0.78rem", fontStyle: "italic" }}>
                        {prefilleddata?.data?.msg_template || selectedTemplate}
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
              <Tooltip title="Preview template">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setPreviewOpen(true)}
                    disabled={!selectedTemplate}
                    sx={{
                      border: "1px solid #D6E3EC",
                      borderRadius: "8px",
                      color: BRAND,
                      "&:hover": { background: "#EAF4FB" },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        )}

        {campaignType !== "realtime" && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={scheduleFileCreation}
                    onChange={(e) => setScheduleFileCreation(e.target.checked)}
                  />
                }
                label="Schedule file creation"
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.78rem" },
                }}
              />
              {scheduleFileCreation && (
                <TextField
                  label="File creation date & time"
                  type="datetime-local"
                  size="small"
                  value={fileCreationDateTime}
                  onChange={(e) => setFileCreationDateTime(e.target.value)}
                  inputProps={{ min: getCurrentISTDateTime().slice(0, 16) }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": glassInput }}
                />
              )}
            </Box>
          </Grid>
        )}

        {campaignType !== "realtime" && (
          <>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#4A5A68", mt: 1, mb: 1 }}>
                <ScheduleRoundedIcon sx={{ fontSize: "0.9rem", verticalAlign: "middle", mr: 0.5 }} />
                Schedule
              </Typography>
            </Grid>

            {campaignType === "once" ? (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Run Date & Time"
                  type="datetime-local"
                  size="small"
                  fullWidth
                  value={scheduleDateTime}
                  onChange={(e) => setScheduleDateTime(e.target.value)}
                  inputProps={{ min: getCurrentISTDateTime().slice(0, 16) }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ "& .MuiOutlinedInput-root": glassInput }}
                />
              </Grid>
            ) : (
              <>
                <Grid item xs={6} sm={2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: "0.7rem" }}>Repeat Period</InputLabel>
                    <Select label="Repeat Period" value={runDuration} onChange={(e) => setRunDuration(e.target.value)} sx={smallSelectSx}>
                      {RUN_DURATION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.78rem" }}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <TextField
                    label=" Run At"
                    type="time"
                    size="small"
                    fullWidth
                    value={scheduleDateTime}
                    onChange={(e) => setScheduleDateTime(e.target.value)}
                    inputProps={{ min: getCurrentISTDateTime().slice(11, 16) }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": glassInput }}
                  />
                </Grid>

                {runDuration === "weekly" && (
                  <Grid item xs={12}>
                    <FormControl size="small" fullWidth>
                      <InputLabel sx={{ fontSize: "0.8rem" }}>Select Days</InputLabel>
                      <Select
                        multiple
                        label="Select Days"
                        value={selectedDays}
                        onChange={(e) => {
                          const v = e.target.value;
                          setSelectedDays(typeof v === "string" ? v.split(",") : v);
                        }}
                        renderValue={(vals) => vals.map((d) => d.charAt(0) + d.slice(1).toLowerCase()).join(", ")}
                        sx={smallSelectSx}
                      >
                        {WEEKDAY_OPTIONS.map((day) => (
                          <MenuItem key={day} value={day} sx={{ fontSize: "0.8rem" }}>
                            <Checkbox checked={selectedDays.indexOf(day) > -1} size="small" />
                            {day.charAt(0) + day.slice(1).toLowerCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {runDuration === "monthly" && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.75 }}>
                      Select Dates of Month
                    </Typography>
                    <CalendarDatePicker
                      selectedDates={selectedDates}
                      onToggleDate={toggleDateSelection}
                    />
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </Grid>
    </Box>
  );

  const renderSplitBatchConfig = () => (
    <Box sx={sectionSx}>
      <Typography sx={sectionLabelSx}>
        <TuneRoundedIcon sx={{ fontSize: "1rem" }} /> Split & Batch Configuration
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={3}></Grid>
        <Grid item xs={6} sm={3}></Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
            <Switch checked={furtherSplit} onChange={(e) => setFurtherSplit(e.target.checked)} size="small" />
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>Further Split</Typography>
          </Box>
        </Grid>
        {furtherSplit && (
          <>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Further Split - No. of Records"
                type="number"
                size="small"
                fullWidth
                value={furtherSplitNoOfRecords}
                onChange={(e) => setFurtherSplitNoOfRecords(e.target.value)}
                onBlur={((e) =>{
                  if(e.target.value === "" || Number(e.target.value) < 1000) {
                    setFurtherSplitNoOfRecords(1000);
                  }
                })}
                helperText="min records are 1000"
                InputProps={{min: 1000}}
                sx={{ "& .MuiOutlinedInput-root": glassInput }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Further Split - Time Gap (min)"
                type="number"
                size="small"
                fullWidth
                value={furtherSplitTimeGap}
                onChange={(e) => setFurtherSplitTimeGap(e.target.value)}
                onBlur={((e) => {
                  if(e.target.value === "" || Number(e.target.value) < 15)
                  {
                    setFurtherSplitTimeGap(15)
                  }
                })}
                helperText="min time is 15 minutes"
                inputProps={{min: 15}}
                sx={{ "& .MuiOutlinedInput-root": glassInput }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );

  const renderFileManagement = () => (
    <Box sx={sectionSx}>
      <Typography sx={sectionLabelSx}>
        <TuneRoundedIcon sx={{ fontSize: "1rem" }} /> File Management
      </Typography>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="filemanage-label" sx={{ fontSize: "0.75rem" }}>File management</InputLabel>
            <Select
              labelId="filemanage-label"
              id="filemanage"
              label="File management"
              value={filemanage}
              onChange={(e) => setFilemanage(e.target.value)}
              sx={smallSelectSx}
            >
              <MenuItem value={0} sx={{ fontSize: "0.78rem" }}>all records in one file</MenuItem>
              <MenuItem value={1} sx={{ fontSize: "0.78rem" }}>all records in equal no. of files</MenuItem>
              <MenuItem value={2} sx={{ fontSize: "0.78rem" }}>given no. of records in given no of files.</MenuItem>
              <MenuItem value={3} sx={{ fontSize: "0.78rem" }}> no of files.</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {filemanage === 2 && (
          <Grid item xs={6} sm={4}>
            <TextField
              label="Records / File"
              type="number"
              size="small"
              fullWidth
              value={recordsPerPage}
              onChange={(e) => setRecordsPerPage(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": glassInput }}
            />
          </Grid>
        )}

        {(filemanage === 1 || filemanage === 2 || filemanage === 3) && (
          <Grid item xs={6} sm={4}>
            <TextField
              label="No of File "
              type="number"
              size="small"
              fullWidth
              value={batchLimit}
              onChange={(e) => setBatchLimit(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": glassInput }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const renderEventsTable = () => (
    <Paper elevation={1} sx={{ borderRadius: 1, overflow: "hidden", mt: 2.5 }}>
      <Box sx={{ p: 1.25, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
          List of Events Created
        </Typography>
        {eventsLoading && (
          <CircularProgress size={18} sx={{ color: BRAND }} />
        )}
      </Box>
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ bgcolor: "#0288d1" }}>
            <TableRow>
              <TableCell sx={thCell}>Event Name</TableCell>
              <TableCell sx={thCell}>Event Type</TableCell>
              <TableCell sx={thCell}>Created / Next Run</TableCell>
              <TableCell sx={thCell}>Fields in File</TableCell>
              <TableCell sx={thCell}>Filter Conditions</TableCell>
              <TableCell sx={thCell} align="center">Status</TableCell>
              <TableCell sx={{ ...thCell, borderRight: "none" }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ background: "rgba(255,255,255,0.23)" }}>
            {paginatedEvents.length > 0 ? (
              paginatedEvents.map((item) => (
                <TableRow key={item.event_id} hover sx={{ "& td": { fontSize: "0.8rem", py: 0.5 }, "&:hover": { background: "rgba(186,230,253,0.2)" } }}>
                  <TableCell sx={tdCell}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                      {item.event_name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                      ID: {item.event_id}
                    </Typography>
                  </TableCell>
                  <TableCell sx={tdCell}><Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                      {item.repeat_type}
                    </Typography></TableCell>
                  <TableCell sx={tdCell}>
                    <Typography sx={{ fontSize: "0.72rem" }}>
                      Created: {item.created_at && dayjs(item.created_at).isValid()
                        ? dayjs(item.created_at).format("DD/MM/YYYY HH:mm")
                        : "-"}
                    </Typography>
                    {String(item.repeat_type).toLowerCase() === "repeat" && (
                      <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                        Next run: {item.next_run_at && dayjs(item.next_run_at).isValid()
                          ? dayjs(item.next_run_at).format("DD/MM/YYYY HH:mm")
                          : "-"}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={tdCell}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {item.data_filter?.requiredData ? (
                        item.data_filter.requiredData.split(",").filter(Boolean).map((f) => (
                          <Chip key={f} label={f} size="small" variant="outlined" sx={{ fontSize: "0.68rem" }} />
                        ))
                      ) : (
                        <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={tdCell}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {item.data_filter?.jsonData?.length > 0 ? (
                        item.data_filter.jsonData.map((filter, idx) => (
                          <Chip
                            key={idx}
                            label={`${filter.fieldName} ${filter.searchType} ${filter.value}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.68rem" }}
                          />
                        ))
                      ) : (
                        <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={tdCell} align="center">
                    {String(item.repeat_type).toLowerCase() === "repeat" ? (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={item.is_active !== false}
                            disabled={isUpdatingEventStatus}
                            inputProps={{ "aria-label": `Toggle event ${item.event_name}` }}
                            onChange={(event) => handleEventStatusChange(item, event.target.checked)}
                          />
                        }
                        label={item.is_active !== false ? "Active" : "Inactive"}
                        sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.68rem" } }}
                      />
                    ) : (
                      <Chip
                        label={item.is_active !== false ? "Active" : "Inactive"}
                        size="small"
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          color: item.is_active !== false ? "#1B7A3D" : "#8A6D00",
                          background: item.is_active !== false ? "#E4F6E9" : "#FFF4D9",
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...tdCell, borderRight: "none" }} align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Edit event">
                        <IconButton size="small" onClick={() => handleEditEvent(item)} sx={{ color: "#0288d1" }}>
                          <EditIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete event">
                        <IconButton size="small" onClick={() => handleDeleteClick(item.event_id)} sx={{ color: "#d32f2f" }}>
                          <DeleteIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={tdCell}>
                  {eventsLoading ? "Loading events..." : "No events created yet."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.5, py: 1 }}>
        <Typography sx={{ fontSize: "0.74rem" }}>
          {totalEvents > 0
            ? `Showing page ${eventsPage} of ${totalPages} (${totalEvents} total)`
            : "No records"}
        </Typography>
        <Pagination
          count={totalPages || 1}
          page={eventsPage}
          onChange={(e, value) => setEventsPage(value)}
          size="small"
          color="primary"
        />
      </Box>
    </Paper>
  );

  return (
    <Box sx={layoutCSS}>
      <Box sx={{ width: "100%", overflowX: "auto", mb: 1.5, mt: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: "0.95rem", fontWeight: 700 }}>
            Event Creation
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreateNewEvent}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              background: `linear-gradient(135deg, ${BRAND} 0%, #01579b 100%)`,
              boxShadow: "0 6px 16px rgba(2,136,209,0.35)",
            }}
          >
            Create New Event
          </Button>
        </Box>

        {showForm && (
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2, mb: 2, background: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)" }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.78rem", fontWeight: 600 }}>
              Campaign Type
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              {CAMPAIGN_TYPES.map(({ value, label, desc, icon: Icon }) => (
                <Button
                  key={value}
                  onClick={() => handleCampaignTypeSelect(value)}
                  sx={{
                    textTransform: "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    borderRadius: "10px",
                    py: 1,
                    px: 1.5,
                    border: campaignType === value ? `2px solid ${BRAND}` : "1px solid #D6E3EC",
                    color: campaignType === value ? BRAND : "#2A3944",
                    background: campaignType === value ? "#EAF4FB" : "#fff",
                    "&:hover": { background: "#EAF4FB", borderColor: BRAND },
                  }}
                >
                  <Icon fontSize="small" />
                  <Box sx={{ textAlign: "left" }}>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", lineHeight: 1.3 }}>{desc}</Typography>
                  </Box>
                </Button>
              ))}
            </Box>

            <Box sx={{ mb: 1.25 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: "0.78rem", fontWeight: 600 }}>Event Name</Typography>
              <TextField size="small" value={eventName} onChange={(e) => setEventName(e.target.value)} sx={nameFieldSx} />
            </Box>

            {campaignType && renderFlowConfiguration()}
            {campaignType && campaignType !== "realtime" && renderSplitBatchConfig()}
            {renderEventDataFilter()}
            {campaignType && campaignType !== "realtime" && renderFileManagement()}

            <Box sx={{ display: "flex", gap: 1.25, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ textTransform: "none", borderRadius: "8px" }}
                onClick={handleSaveEvent}
                disabled={isSavingFlow}
                startIcon={isSavingFlow ? <CircularProgress size={14} color="inherit" /> : null}
              >
                {editingEventId ? (isSavingFlow ? "Updating..." : "Update Event") : isSavingFlow ? "Saving..." : "Save Event"}
              </Button>
              <Button variant="outlined" size="small" sx={{ textTransform: "none", borderRadius: "8px" }} onClick={handleCancelForm}>
                Cancel
              </Button>
            </Box>
          </Paper>
        )}
      </Box>

      {renderEventsTable()}

      {serviceType === "whatsapp" || serviceType === "rcs" || serviceType === "sms" ? (
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth={false}
          sx={{
            "& .MuiDialog-paper": { bgcolor: "transparent", boxShadow: "none", p: 0, overflow: "visible" },
            "& .MuiBackdrop-root": { bgcolor: "rgba(0,0,0,0.65)" },
          }}
        >
          <DialogContent
            sx={{
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              pt: 5,
              bgcolor: "transparent",
              overflow: "visible",
              position: "relative",
            }}
          >
            {serviceType === "whatsapp"
              ? renderWhatsAppPhonePreview()
              : serviceType === "rcs"
              ? renderRcsPhonePreview()
              : renderSmsPhonePreview()}

            <IconButton
              onClick={() => setPreviewOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#fff",
                bgcolor: "rgba(0,0,0,0.45)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: {
            borderRadius: "14px",
            boxShadow: "0 24px 60px rgba(2, 136, 209, 0.18), 0 4px 12px rgba(0,0,0,0.06)",
            overflow: "hidden",
          } }}
        >
          <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2.5,
            py: 1.5,
            background: `linear-gradient(135deg, ${BRAND} 0%, #01579b 100%)`,
            color: "#fff",
          }}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", width: 34, height: 34 }}>
              <VisibilityIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "0.68rem", opacity: 0.85, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                Template Preview
              </Typography>
              <Typography sx={{ fontSize: "0.95rem", fontWeight: 700 }}>
                {serviceType ? SERVICE_OPTIONS.find((s) => s.value === serviceType)?.label : "Preview"}
              </Typography>
            </Box>
            <IconButton onClick={() => setPreviewOpen(false)} size="small" sx={{ color: "#fff" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <DialogContent sx={{ p: 2.5, background: "#F4F8FB" }}>
            <Box sx={{ ...sectionSx, background: "#fff", mt: 0 }}>
              {serviceType === "flow" ? (
                (() => {
                  const fObj = (flowsResp?.data || []).find((ff) => String(ff.cf_id || ff.flow_id) === String(selectedFlow)) || fetchedFlows.find((ff) => ff.flow_id === selectedFlow) || null;
                  return (
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{fObj?.flow_name || "No flow selected"}</Typography>
                      {fObj?.category && <Chip size="small" label={`Category: ${fObj.category}`} sx={{ fontSize: "0.72rem", background: "#EEF6FC", mt: 1 }} />}
                    </Box>
                  );
                })()
              ) : (
                <Typography color="text.secondary">No preview</Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, py: 1.5, background: "#F4F8FB", borderTop: "1px solid #E6ECF1" }}>
            <Button
              onClick={() => setPreviewOpen(false)}
              variant="outlined"
              size="small"
              sx={{ textTransform: "none", borderRadius: "10px", fontSize: "0.8rem" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: {
          borderRadius: "14px",
          boxShadow: "0 24px 60px rgba(2, 136, 209, 0.18), 0 4px 12px rgba(0,0,0,0.06)",
          overflow: "hidden",
        } }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          <Avatar
            sx={{
              bgcolor: "#FDECEA",
              color: "#D32F2F",
              width: 48,
              height: 48,
              mx: "auto",
              mb: 1.5,
            }}
          >
            <WarningAmberRoundedIcon fontSize="medium" />
          </Avatar>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, mb: 1 }}>
            Delete this event?
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
            {deleteTargetEvent?.event_name ? (
              <>
                <strong>{deleteTargetEvent.event_name}</strong> and its saved flow configuration will be permanently removed. This action cannot be undone.
              </>
            ) : (
              "This action cannot be undone."
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 0, justifyContent: "center", gap: 1.25 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            size="small"
            sx={{ textTransform: "none", fontSize: "0.8rem", borderRadius: "10px", flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            sx={{ textTransform: "none", fontSize: "0.8rem", borderRadius: "10px", flex: 1, boxShadow: "0 6px 16px rgba(211,47,47,0.3)" }}
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EventCreation;